import React from 'react';
import { FormattedMessage } from 'react-intl';
import { generatePath } from 'react-router';

import { serpsRoutes } from '@/config';
import {
  CityAggregationFragment,
  DisciplineAggregationFragment,
  EmploymentTypeAggregationFragment,
  RoleAggregationFragment,
  TopCategoriesQueryHookResult,
  TopSubcategoriesQueryHookResult,
} from '@/graphql/generated';

import { ANY, CITY, DISCIPLINE, EMPLOYMENT_TYPE, ROLE } from '../constants'; // COMPANY will not be used here for now
import { SerpsCategory, SerpsCategoryDataField, SerpsCategoryName, SerpsLink, SerpsListItem } from '../types';
import {
  deslugify,
  disciplineIdToCanonicalSlug,
  disciplineIdToLocalizedKey,
  disciplineSlugToLocalizedKey,
  employmentTypeIdToCanonicalSlug,
  employmentTypeIdToLocalizedKey,
  employmentTypeSlugToLocalizedKey,
  slugify,
} from './static-data-utils';

type CategoryAggregation =
  | CityAggregationFragment
  | DisciplineAggregationFragment
  | EmploymentTypeAggregationFragment
  | RoleAggregationFragment;

type GenerateCategoryListItemsParams = {
  data: TopCategoriesQueryHookResult['data'] | TopSubcategoriesQueryHookResult['data'];
  dataField: SerpsCategoryDataField;
  categoryName: SerpsCategoryName;
  parentCategory?: SerpsCategory;
};

const generateMultiPartLink = (category: SerpsCategory, parentCategory: SerpsCategory): SerpsLink | null => {
  if (!category.value) return null;

  const isTemporaryJob = category.value.includes('TEMPORARY');

  switch (`${parentCategory.name}-${category.name}`) {
    case `${DISCIPLINE}-${ROLE}`:
      return {
        url: generatePath(serpsRoutes.disciplineRole, {
          categoryValue: parentCategory.value,
          subcategoryValue: slugify(category.value),
        }),
        label: (
          <>
            {category.value}
            <FormattedMessage id="XJM_TEASER_LINKS_JOBS_IN" values={{ strong: (chunks) => chunks }} />{' '}
            <FormattedMessage id={disciplineSlugToLocalizedKey(parentCategory.value)} />
          </>
        ),
      };
    case `${CITY}-${EMPLOYMENT_TYPE}`: {
      let url = generatePath(serpsRoutes.employmentTypeCity, {
        categoryValue: employmentTypeIdToCanonicalSlug(category.value),
        subcategoryValue: parentCategory.value,
      });
      let employmentTypeTranslationKey = employmentTypeIdToLocalizedKey(category.value);

      if (category.value.includes('CONTRACTOR')) {
        employmentTypeTranslationKey = 'EMPLOYMENT_TYPE_CONTRACTOR_LOCAL_SERPS';
      }
      if (category.value.includes('TEMPORARY')) {
        employmentTypeTranslationKey = 'EMPLOYMENT_TYPE_TEMPORARY_LOCAL_SERPS';
        url = generatePath(serpsRoutes.roleCity, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: generatePath types are not correct for multiple params in the same level: `/jobs/:categoryValue-jobs-in-:subcategoryValue`
          categoryValue: 'minijob',
          subcategoryValue: parentCategory.value,
        });
      }

      return {
        url,
        label: (
          <>
            <FormattedMessage id={employmentTypeTranslationKey} />
            <FormattedMessage id="XJM_TEASER_LINKS_JOBS_IN" values={{ strong: (chunks) => chunks }} />{' '}
            {deslugify(parentCategory.value)}
          </>
        ),
      };
    }
    case `${DISCIPLINE}-${EMPLOYMENT_TYPE}`:
      return {
        url: generatePath(serpsRoutes.employmentTypeDiscipline, {
          categoryValue: employmentTypeIdToCanonicalSlug(category.value),
          subcategoryValue: parentCategory.value,
        }),
        label: (
          <>
            <FormattedMessage id={employmentTypeIdToLocalizedKey(category.value)} />
            <FormattedMessage id="XJM_TEASER_LINKS_JOBS_IN" values={{ strong: (chunks) => chunks }} />{' '}
            <FormattedMessage id={disciplineSlugToLocalizedKey(parentCategory.value)} />
          </>
        ),
      };
    case `${ROLE}-${EMPLOYMENT_TYPE}`:
      return {
        url: generatePath(serpsRoutes.employmentTypeRole, {
          categoryValue: employmentTypeIdToCanonicalSlug(category.value),
          subcategoryValue: parentCategory.value,
        }),
        label: (
          <>
            {deslugify(parentCategory.value)}{' '}
            {isTemporaryJob ? null : <FormattedMessage id={employmentTypeIdToLocalizedKey(category.value)} />}
            <FormattedMessage id={isTemporaryJob ? 'MP_000_RESOURCE_JOB_TYPES_TEMPORARY' : 'XJM_TEASER_LINKS_JOBS'} />
          </>
        ),
      };
    case `${EMPLOYMENT_TYPE}-${ROLE}`:
      return {
        url: generatePath(serpsRoutes.employmentTypeRole, {
          categoryValue: parentCategory.value,
          subcategoryValue: slugify(category.value),
        }),
        label: (
          <>
            {category.value}
            <FormattedMessage id="XJM_TEASER_LINKS_JOBS_IN" values={{ strong: (chunks) => chunks }} />{' '}
            <FormattedMessage id={employmentTypeSlugToLocalizedKey(parentCategory.value)} />
          </>
        ),
      };
    case `${CITY}-${ROLE}`:
      return {
        url: generatePath(serpsRoutes.roleCity, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: generatePath types are not correct for multiple params in the same level: `/jobs/:categoryValue-jobs-in-:subcategoryValue`
          categoryValue: slugify(category.value),
          subcategoryValue: parentCategory.value,
        }),
        label: (
          <>
            {category.value}
            <FormattedMessage id="XJM_TEASER_LINKS_JOBS_IN" values={{ strong: (chunks) => chunks }} />{' '}
            {deslugify(parentCategory.value)}
          </>
        ),
      };
    case `${ROLE}-${CITY}`:
      return {
        url: generatePath(serpsRoutes.roleCity, {
          // eslint-disable-next-line @typescript-eslint/ban-ts-comment
          // @ts-ignore: generatePath types are not correct for multiple params in the same level: `/jobs/:categoryValue-jobs-in-:subcategoryValue`
          categoryValue: parentCategory.value,
          subcategoryValue: slugify(category.value),
        }),
        label: (
          <>
            {deslugify(parentCategory.value)}
            <FormattedMessage id="XJM_TEASER_LINKS_JOBS_IN" values={{ strong: (chunks) => chunks }} />{' '}
            {deslugify(category.value)}
          </>
        ),
      };
    default:
      return null;
  }
};

const generateSinglePartLink = (category: SerpsCategory): SerpsLink | null => {
  if (!category.value) return null;

  switch (category.name) {
    case CITY:
      return {
        url: generatePath(serpsRoutes.city, {
          categoryValue: slugify(category.value),
        }),
        label: category.value,
      };
    case DISCIPLINE:
      return {
        url: generatePath(serpsRoutes.discipline, {
          categoryValue: disciplineIdToCanonicalSlug(category.value),
        }),
        label: <FormattedMessage id={disciplineIdToLocalizedKey(category.value)} />,
      };
    case EMPLOYMENT_TYPE:
      return {
        url: generatePath(serpsRoutes.employmentType, {
          categoryValue: employmentTypeIdToCanonicalSlug(category.value),
        }),
        label: <FormattedMessage id={employmentTypeIdToLocalizedKey(category.value)} />,
      };
    case ROLE:
      return {
        url: generatePath(serpsRoutes.role, {
          categoryValue: slugify(category.value),
        }),
        label: category.value,
      };
    case ANY:
      return {
        url: generatePath(serpsRoutes.any, {
          categoryValue: slugify(category.value),
        }),
        label: category.value,
      };
    default:
      return null;
  }
};

const generateItemByCategory = (
  result: CategoryAggregation,
  category: SerpsCategory,
  parentCategory?: SerpsCategory
): SerpsListItem => ({
  id: result.id,
  count: result.count,
  link: parentCategory ? generateMultiPartLink(category, parentCategory) : generateSinglePartLink(category),
});

const generateItem = (
  result: CategoryAggregation,
  categoryName: SerpsCategoryName,
  parentCategory?: SerpsCategory
): SerpsListItem | null => {
  switch (categoryName) {
    case CITY:
      return result[categoryName]
        ? generateItemByCategory(
            result,
            { name: categoryName, value: (result as CityAggregationFragment).city?.localizationValue ?? '' },
            parentCategory
          )
        : null;
    case DISCIPLINE:
      return generateItemByCategory(result, { name: categoryName, value: result.id }, parentCategory);
    case EMPLOYMENT_TYPE:
      return generateItemByCategory(result, { name: categoryName, value: result.id }, parentCategory);
    case ROLE:
    case ANY:
      return result[categoryName]
        ? generateItemByCategory(
            result,
            { name: categoryName, value: (result as RoleAggregationFragment).role?.localizationValue ?? '' },
            parentCategory
          )
        : null;
    default:
      return null;
  }
};

export const generateCategoryListItems = ({
  data,
  dataField,
  categoryName,
  parentCategory,
}: GenerateCategoryListItemsParams): SerpsListItem[] => {
  const results: CategoryAggregation[] = data?.jobSearchByQuery?.aggregations?.[dataField] ?? [];

  return results
    .map((result) => generateItem(result, categoryName, parentCategory))
    .filter((result): result is SerpsListItem => result !== null)
    .sort((a, b) => b.count - a.count);
};
